import { useDispatch } from "react-redux";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { v4 as uuid } from "uuid";

import { apiThatThrows } from "containers/Request";
import { ApiRequestError, ApiResponse } from "types/api";
import { createAtomicOperationsBody } from "utils/jsonApi";

import {
  ApiServerErrorMetaMatchingVariantVersion,
  TiVoAppVariant,
  TiVoAppVariantVersion,
  TiVoAppVariantVersionInput,
} from "./types";

export const useCreateVariant = (id?: string) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<
    TiVoAppVariant,
    ApiRequestError<ApiServerErrorMetaMatchingVariantVersion>,
    Partial<TiVoAppVariantVersionInput>
  >({
    mutationFn: async (data) => {
      const lid = `${data.label}-${uuid()}`; // essential for creating a variant and its version together
      const strict = data.strict ?? true;
      const { results }: ApiResponse<[TiVoAppVariant, TiVoAppVariantVersion]> =
        await dispatch(
          apiThatThrows.createTiVoVariant.action({
            params: { id },
            options: {
              body: createAtomicOperationsBody([
                {
                  op: "add",
                  data: {
                    type: "variant",
                    lid,
                    attributes: { label: data.label },
                  },
                },
                {
                  op: "add",
                  ref: { type: "variant", lid },
                  data: {
                    type: "variant_version",
                    attributes: { ...data, strict },
                  },
                },
              ]),
            },
          })
        );
      return results[0];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["providers"] });
    },
  });
};
